import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Users, IndianRupee, Phone, Mail, User, CheckCircle, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';
import { CONTACT } from '../lib/butterstone';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROOM_TYPE_LABEL = { couple: 'Couple Room', deluxe: 'Deluxe', dormitory: 'Dormitory', tent: 'Camping Tent' };

const inputCls = 'pl-10 bg-white border-[#2F5233]/25 text-[#1C2A20] focus:border-[#2F5233]';

const BookingModal = ({ isOpen, onClose, room }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [allRooms, setAllRooms] = useState([]);
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [formData, setFormData] = useState({
    guest_name: '', email: '', phone: '',
    check_in_date: null, check_out_date: null, num_guests: 2, special_requests: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API_URL}/api/rooms`); setAllRooms(res.data); }
      catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (room) { setSelectedRoomIds([room.id]); setFormData(prev => ({ ...prev, num_guests: Math.min(2, room.capacity || 2) })); }
  }, [room]);

  useEffect(() => {
    if (!isOpen) { setStep(1); setSuccess(false); setErrors({}); }
  }, [isOpen]);

  const roomsList = allRooms.length ? allRooms : (room ? [room] : []);
  const selectedRooms = roomsList.filter(r => selectedRoomIds.includes(r.id));
  const maxGuests = selectedRooms.reduce((sum, r) => sum + (r.capacity || 0), 0) || 1;

  const nights = () => {
    if (!formData.check_in_date || !formData.check_out_date) return 0;
    return Math.max(Math.ceil((new Date(formData.check_out_date) - new Date(formData.check_in_date)) / (1000 * 60 * 60 * 24)), 1);
  };

  // Per-head estimate: guests split across selected rooms use the average per-head rate.
  const avgPerHead = selectedRooms.length ? Math.round(selectedRooms.reduce((s, r) => s + (r.price_per_head || 0), 0) / selectedRooms.length) : 0;
  const calculateTotal = () => {
    const n = nights();
    const g = typeof formData.num_guests === 'number' ? formData.num_guests : 0;
    if (!n || !selectedRooms.length || !g) return 0;
    return avgPerHead * g * n;
  };

  useEffect(() => {
    if (typeof formData.num_guests === 'number' && formData.num_guests > maxGuests) {
      setFormData(prev => ({ ...prev, num_guests: maxGuests }));
    }
  }, [maxGuests]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRoom = (r) => {
    setSelectedRoomIds(prev => prev.includes(r.id) ? prev.filter(id => id !== r.id) : [...prev, r.id]);
    if (errors.rooms) setErrors(prev => ({ ...prev, rooms: null }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!formData.guest_name.trim()) e.guest_name = 'Name is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Invalid email format';
    if (!formData.phone.trim()) e.phone = 'Phone is required';
    else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) e.phone = 'Please enter a valid 10-digit phone number';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!selectedRoomIds.length) e.rooms = 'Please select at least one room';
    if (!formData.check_in_date) e.check_in_date = 'Check-in date is required';
    if (!formData.check_out_date) e.check_out_date = 'Check-out date is required';
    if (formData.check_in_date && formData.check_out_date && new Date(formData.check_in_date) >= new Date(formData.check_out_date)) e.check_out_date = 'Check-out must be after check-in';
    if (formData.num_guests < 1) e.num_guests = 'At least 1 guest required';
    if (formData.num_guests > maxGuests) e.num_guests = `Maximum ${maxGuests} guests for the selected room(s)`;
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingData = {
        guest_name: formData.guest_name, email: formData.email, phone: formData.phone,
        check_in_date: formData.check_in_date ? format(formData.check_in_date, 'yyyy-MM-dd') : '',
        check_out_date: formData.check_out_date ? format(formData.check_out_date, 'yyyy-MM-dd') : '',
        num_guests: formData.num_guests,
        room_type: (selectedRooms[0] || room).room_type,
        room_id: (selectedRooms[0] || room).id,
        room_name: (selectedRooms[0] || room).name,
        room_ids: selectedRoomIds,
        room_names: selectedRooms.map(r => r.name),
        special_requests: formData.special_requests?.trim() || '',
        total_price: calculateTotal()
      };
      await axios.post(`${API_URL}/api/bookings`, bookingData);
      setSuccess(true); setStep(4);
    } catch (error) {
      console.error('Booking error:', error);
      setErrors({ submit: 'Failed to submit booking. Please try again.' });
    } finally { setLoading(false); }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  if (!room) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#F6F5EF] border-[#2F5233]/25 max-h-[90vh] overflow-y-auto text-[#1C2A20]">
        <DialogHeader>
          <div className="overline mb-2">{success ? 'Confirmed' : 'Reservation'}</div>
          <DialogTitle className="font-serif text-2xl text-[#1E3226] font-medium">
            {success ? 'Booking received' : room.name}
          </DialogTitle>
        </DialogHeader>

        {!success && (
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 flex items-center justify-center text-xs font-medium border rounded-full transition-colors ${step >= s ? 'bg-[#2F5233] text-white border-[#2F5233]' : 'bg-transparent text-[#5E6E60] border-[#2F5233]/30'}`}>{s}</div>
                {s < 3 && <div className={`w-16 h-px mx-2 ${step > s ? 'bg-[#2F5233]' : 'bg-[#2F5233]/20'}`} />}
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2F5233]" size={16} />
                <Input value={formData.guest_name} onChange={(e) => handleChange('guest_name', e.target.value)} placeholder="Enter your full name" className={inputCls} data-testid="booking-name-input" />
              </div>
              {errors.guest_name && <p className="text-red-500 text-xs mt-1">{errors.guest_name}</p>}
            </div>
            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2F5233]" size={16} />
                <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="your.email@example.com" className={inputCls} data-testid="booking-email-input" />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Phone Number</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2F5233]" size={16} />
                <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="10-digit mobile number" className={inputCls} data-testid="booking-phone-input" />
              </div>
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>
            <Button onClick={handleNext} className="w-full bg-[#2F5233] hover:bg-[#1E3226] text-white uppercase tracking-[0.2em] text-xs font-semibold py-6" data-testid="booking-next-step1">Continue</Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Select Room(s)</Label>
              <p className="text-[10px] text-[#5E6E60] mt-1 mb-2 tracking-wider">Choose one or more rooms for your stay.</p>
              <div className="space-y-2" data-testid="booking-rooms-list">
                {roomsList.map((r) => {
                  const selected = selectedRoomIds.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRoom(r)} className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left border rounded-md transition-colors ${selected ? 'border-[#2F5233] bg-white' : 'border-[#2F5233]/20 bg-white/50'}`} data-testid={`booking-room-select-${r.id}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-5 h-5 flex items-center justify-center border rounded ${selected ? 'bg-[#2F5233] border-[#2F5233]' : 'border-[#2F5233]/40'}`}>{selected && <Check size={13} className="text-white" />}</span>
                        <span>
                          <span className="text-[#1C2A20] text-sm">{r.name}</span>
                          <span className="block text-[10px] tracking-wider uppercase text-[#6E9A5E]">{ROOM_TYPE_LABEL[r.room_type] || r.room_type} · sleeps {r.capacity}</span>
                        </span>
                      </div>
                      <span className="text-[#2F5233] text-sm numeral">₹{(r.price_per_head || 0).toLocaleString()}<span className="text-[10px] text-[#5E6E60]">/head</span></span>
                    </button>
                  );
                })}
              </div>
              {errors.rooms && <p className="text-red-500 text-xs mt-1">{errors.rooms}</p>}
            </div>

            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Check-in Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal mt-1 bg-white border-[#2F5233]/25 text-[#1C2A20] hover:bg-[#EAF0E4]" data-testid="booking-checkin-btn">
                    <Calendar className="mr-2 h-4 w-4 text-[#2F5233]" />
                    {formData.check_in_date ? format(formData.check_in_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#2F5233]/25 z-[200]" align="start">
                  <CalendarComponent mode="single" selected={formData.check_in_date} onSelect={(date) => {
                    handleChange('check_in_date', date);
                    if (formData.check_out_date && date && new Date(formData.check_out_date) <= new Date(date)) handleChange('check_out_date', null);
                  }} disabled={(date) => date < new Date()} initialFocus />
                </PopoverContent>
              </Popover>
              {errors.check_in_date && <p className="text-red-500 text-xs mt-1">{errors.check_in_date}</p>}
            </div>

            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Check-out Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal mt-1 bg-white border-[#2F5233]/25 text-[#1C2A20] hover:bg-[#EAF0E4]" data-testid="booking-checkout-btn">
                    <Calendar className="mr-2 h-4 w-4 text-[#2F5233]" />
                    {formData.check_out_date ? format(formData.check_out_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#2F5233]/25 z-[200]" align="start">
                  <CalendarComponent mode="single" selected={formData.check_out_date} onSelect={(date) => handleChange('check_out_date', date)} disabled={(date) => date <= (formData.check_in_date || new Date())} initialFocus />
                </PopoverContent>
              </Popover>
              {errors.check_out_date && <p className="text-red-500 text-xs mt-1">{errors.check_out_date}</p>}
            </div>

            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Number of Guests</Label>
              <div className="relative mt-1">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2F5233]" size={16} />
                <Input type="number" min={1} max={maxGuests} value={formData.num_guests === '' ? '' : formData.num_guests}
                  onChange={(e) => { const v = e.target.value; handleChange('num_guests', v === '' ? '' : parseInt(v) || ''); }}
                  onBlur={() => { if (formData.num_guests === '' || formData.num_guests < 1) handleChange('num_guests', 1); }}
                  className={inputCls} data-testid="booking-guests-input" />
              </div>
              <p className="text-[10px] text-[#5E6E60] mt-1 tracking-wider">Up to {maxGuests} guests for the selected room(s) · priced per head</p>
              {errors.num_guests && <p className="text-red-500 text-xs mt-1">{errors.num_guests}</p>}
            </div>

            <div>
              <Label className="text-[#5E6E60] text-xs tracking-wider uppercase">Special Requests (Optional)</Label>
              <Textarea value={formData.special_requests} onChange={(e) => handleChange('special_requests', e.target.value)} placeholder="Any special requirements..." className="mt-1 bg-white border-[#2F5233]/25 text-[#1C2A20] focus:border-[#2F5233] min-h-[80px]" data-testid="booking-requests-input" />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-[#2F5233]/30 bg-transparent text-[#2F5233] hover:bg-[#EAF0E4] uppercase tracking-[0.2em] text-xs" data-testid="booking-back-step2">Back</Button>
              <Button onClick={handleNext} className="flex-1 bg-[#2F5233] hover:bg-[#1E3226] text-white uppercase tracking-[0.2em] text-xs font-semibold" data-testid="booking-next-step2">Review</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-white border border-[#2F5233]/20 p-5 rounded-md">
              <div className="overline mb-4">Summary</div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between items-start"><span className="text-[#5E6E60]">Room(s)</span><span className="text-[#1C2A20] text-right">{selectedRooms.map(r => <span key={r.id} className="block">{r.name}</span>)}</span></div>
                <div className="flex justify-between"><span className="text-[#5E6E60]">Guest</span><span className="text-[#1C2A20]">{formData.guest_name}</span></div>
                <div className="flex justify-between"><span className="text-[#5E6E60]">Check-in</span><span className="text-[#1C2A20]">{formData.check_in_date ? format(formData.check_in_date, 'PPP') : '-'}</span></div>
                <div className="flex justify-between"><span className="text-[#5E6E60]">Check-out</span><span className="text-[#1C2A20]">{formData.check_out_date ? format(formData.check_out_date, 'PPP') : '-'}</span></div>
                <div className="flex justify-between"><span className="text-[#5E6E60]">Nights</span><span className="text-[#1C2A20]">{nights()}</span></div>
                <div className="flex justify-between"><span className="text-[#5E6E60]">Guests</span><span className="text-[#1C2A20]">{formData.num_guests}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#2F5233]/20">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-[#5E6E60]">Est. Total</span>
                  <div className="flex items-center text-[#2F5233]"><IndianRupee size={18} /><span className="text-2xl numeral">{calculateTotal().toLocaleString()}</span></div>
                </div>
                <p className="text-[10px] text-[#5E6E60] mt-1 tracking-wider">₹{avgPerHead.toLocaleString()}/head × {formData.num_guests || 0} × {nights()}n · Taxes extra · Final amount confirmed by our team</p>
              </div>
            </div>
            {errors.submit && <p className="text-red-500 text-xs text-center">{errors.submit}</p>}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1 border-[#2F5233]/30 bg-transparent text-[#2F5233] hover:bg-[#EAF0E4] uppercase tracking-[0.2em] text-xs" data-testid="booking-back-step3">Back</Button>
              <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#2F5233] hover:bg-[#1E3226] text-white uppercase tracking-[0.2em] text-xs font-semibold" data-testid="booking-submit-btn">{loading ? 'Submitting…' : 'Submit'}</Button>
            </div>
          </div>
        )}

        {step === 4 && success && (
          <div className="text-center py-6">
            <div className="w-14 h-14 border border-[#2F5233]/40 rounded-full flex items-center justify-center mx-auto mb-5"><CheckCircle size={28} className="text-[#2F5233]" /></div>
            <h3 className="font-serif text-2xl text-[#1E3226] mb-2 italic">Thank you.</h3>
            <p className="text-[#5E6E60] mb-6 text-sm leading-relaxed">Your enquiry has reached us. We will be in touch shortly to confirm.</p>
            <div className="bg-white border border-[#2F5233]/20 p-4 text-left mb-6 space-y-1.5 rounded-md">
              <p className="text-xs text-[#5E6E60]"><span className="text-[#2F5233] font-medium">Call · WhatsApp</span> {CONTACT.phone1Display} · {CONTACT.phone2Display}</p>
              <p className="text-xs text-[#5E6E60] break-all"><span className="text-[#2F5233] font-medium">Email</span> {CONTACT.email}</p>
            </div>
            <Button onClick={onClose} className="w-full bg-[#2F5233] hover:bg-[#1E3226] text-white uppercase tracking-[0.2em] text-xs font-semibold" data-testid="booking-close-success">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
